// Opt-in cloud LLM engine: Google Gemini via the Generative Language REST
// API, called directly from the browser with the editor's own key (BYOK).
//
// Implements the SAME LlmClient interface as the on-device WebLLM client, so
// enrichment.ts / SkimPanel / LlmStatusCard are unchanged. Differences from
// the on-device engine:
//   - No multi-GB download / WebGPU. "Loaded" just means "a key is present".
//   - The prompt (which contains manuscript excerpts) leaves the device for
//     Google. This engine is non-default and consent-gated upstream
//     (settings.llmProvider + the Settings warning). ADR-0004 2026-05-17.
//
// Like the WebLLM client we do NOT rely on a provider JSON mode to guarantee
// the key-set: the schema is injected into the system instruction and
// extractJson + the output-validator enforce structure. We DO ask Gemini for
// responseMimeType application/json (it honours it well) as a belt-and-braces.

import { extractJson } from './extract-json';
import type { ChatRequest, ChatResponse, EngineState, LlmClient } from './types';

const GEMINI_MODEL = 'gemini-2.5-flash';
const ENDPOINT = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

interface GeminiResponse {
  candidates?: Array<{
    content?: { parts?: Array<{ text?: string }> };
    finishReason?: string;
  }>;
  promptFeedback?: { blockReason?: string };
  usageMetadata?: { totalTokenCount?: number };
  error?: {
    code?: number;
    message?: string;
    status?: string;
    details?: Array<{ '@type'?: string; retryDelay?: string }>;
  };
}

const RETRYABLE = new Set([429, 500, 503]);
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Parse Google's RetryInfo ("27s" / "1.5s") or a Retry-After header → ms. */
function retryDelayMs(data: GeminiResponse, res: Response | null): number | null {
  const d = data.error?.details?.find((x) => /RetryInfo/i.test(x['@type'] ?? ''))?.retryDelay;
  if (d) {
    const m = /([\d.]+)\s*s/i.exec(d);
    if (m) return Math.round(parseFloat(m[1]) * 1000);
  }
  const ra = res?.headers.get('retry-after');
  if (ra && /^\d+$/.test(ra.trim())) return parseInt(ra.trim(), 10) * 1000;
  return null;
}

/**
 * @param getApiKey reads the current BYOK key (from settings, via the
 *   provider switch in client.ts) at call time — so a key entered after the
 *   client was created is picked up without recreating it.
 */
export function createGeminiClient(getApiKey: () => string): LlmClient {
  let state: EngineState = { kind: 'absent', reason: 'Add your Google Gemini API key in Settings.' };
  const listeners = new Set<(s: EngineState) => void>();

  function setState(next: EngineState): void {
    state = next;
    for (const l of listeners) l(state);
  }

  function syncKeyState(): boolean {
    const hasKey = getApiKey().trim().length > 0;
    if (!hasKey && state.kind !== 'absent') {
      setState({ kind: 'absent', reason: 'Add your Google Gemini API key in Settings.' });
    }
    return hasKey;
  }

  async function ensureLoaded(): Promise<void> {
    if (!syncKeyState()) {
      throw new Error('No Gemini API key configured.');
    }
    if (state.kind !== 'ready') {
      setState({
        kind: 'ready',
        modelId: GEMINI_MODEL,
        loadedAt: Date.now(),
        engineVersion: 'gemini-v1beta',
      });
    }
  }

  // Free-tier resilience. The enrichment pass fires ~12 calls; Gemini's free
  // tier has a low requests-per-minute cap, so a parallel burst 429s almost
  // everything. We therefore (a) SERIALISE all calls through one queue, so
  // the on-device-style "one at a time" contract holds; (b) RETRY 429/503
  // with backoff, honouring Google's own RetryInfo/Retry-After; and (c)
  // CIRCUIT-BREAK: once the quota is genuinely exhausted, fail the remaining
  // queued calls fast with the clear message instead of each stalling
  // through its full backoff (which would freeze enrichment for minutes).
  let queue: Promise<unknown> = Promise.resolve();
  let quotaTrippedUntil = 0;
  const QUOTA_COOLDOWN_MS = 60_000;
  const BACKOFF_MS = [2_000, 6_000, 15_000];
  const RATE_MSG = 'Gemini rate/quota limit hit (HTTP 429). Wait, or use a paid Google tier.';

  function enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = queue.then(fn, fn);
    queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  async function runChat(req: ChatRequest): Promise<ChatResponse> {
    const apiKey = getApiKey().trim();
    if (!apiKey) {
      setState({ kind: 'absent', reason: 'Add your Google Gemini API key in Settings.' });
      throw new Error('No Gemini API key configured.');
    }
    // Circuit breaker: quota tripped recently → fail fast, don't backoff-stall
    // every remaining call in the batch.
    if (Date.now() < quotaTrippedUntil) {
      setState({ kind: 'error', modelId: GEMINI_MODEL, message: RATE_MSG });
      throw new Error(RATE_MSG);
    }

    const systemPrompt = req.schema
      ? `${req.systemPrompt}\n\nRespond ONLY with a single JSON object matching this schema (no prose, no markdown fences):\n${JSON.stringify(req.schema)}`
      : req.systemPrompt;
    const body = JSON.stringify({
      system_instruction: { parts: [{ text: systemPrompt }] },
      contents: [{ role: 'user', parts: [{ text: req.userPrompt }] }],
      generationConfig: {
        temperature: 0,
        maxOutputTokens: req.maxTokens ?? 512,
        responseMimeType: req.schema ? 'application/json' : 'text/plain',
        // Gemini 2.5 Flash "thinks" by default; the thinking tokens eat the
        // output budget and corrupt/empty the structured JSON reply (the
        // "classify_study not parseable JSON" failure). These are extraction
        // tasks — no chain-of-thought needed. Disabling it also cuts latency
        // and quota use, which directly helps the 429 problem.
        thinkingConfig: { thinkingBudget: 0 },
      },
    });

    const maxAttempts = BACKOFF_MS.length + 1;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let res: Response | null = null;
      let netErr: unknown = null;
      try {
        res = await fetch(`${ENDPOINT}?key=${encodeURIComponent(apiKey)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
      } catch (err) {
        netErr = err;
      }

      let data: GeminiResponse = {};
      if (res) {
        try {
          data = (await res.json()) as GeminiResponse;
        } catch {
          data = {};
        }
      }

      const failed = netErr !== null || !res || !res.ok || Boolean(data.error);
      if (!failed && res) {
        const text = (data.candidates?.[0]?.content?.parts ?? [])
          .map((p) => p.text ?? '')
          .join('')
          .trim();
        if (!text) {
          const blocked = data.promptFeedback?.blockReason;
          throw new Error(
            blocked
              ? `Gemini blocked the response (${blocked}).`
              : 'Gemini returned an empty response.',
          );
        }
        quotaTrippedUntil = 0; // healthy round-trip clears the breaker
        if (state.kind !== 'ready') {
          setState({
            kind: 'ready',
            modelId: GEMINI_MODEL,
            loadedAt: Date.now(),
            engineVersion: 'gemini-v1beta',
          });
        }
        const json = req.schema ? extractJson(text) : undefined;
        return { text, json, tokens: data.usageMetadata?.totalTokenCount };
      }

      const code = netErr ? 0 : (data.error?.code ?? res?.status ?? 0);
      const apiMsg = data.error?.message ?? res?.statusText ?? 'network error';
      const isRetryable = netErr !== null || RETRYABLE.has(code);

      if (isRetryable && attempt < maxAttempts - 1) {
        const delay = retryDelayMs(data, res) ?? BACKOFF_MS[attempt];
        await sleep(Math.min(Math.max(delay, 1_000), 30_000));
        continue;
      }

      // Out of retries (or a non-retryable error) — classify and throw.
      let message: string;
      if (code === 400 || code === 403) {
        message = `Google rejected the API key (HTTP ${code}). Check the key in Settings and that the Generative Language API is enabled for it.`;
      } else if (code === 429) {
        message = RATE_MSG;
        quotaTrippedUntil = Date.now() + QUOTA_COOLDOWN_MS;
      } else if (netErr) {
        message =
          'Could not reach Google Gemini (network error). The pattern-only result still stands.';
      } else {
        message = `Gemini request failed (HTTP ${code}: ${apiMsg}).`;
      }
      setState({ kind: 'error', modelId: GEMINI_MODEL, message });
      throw new Error(message);
    }
    // Unreachable, but satisfies the type checker.
    throw new Error(RATE_MSG);
  }

  function chat(req: ChatRequest): Promise<ChatResponse> {
    return enqueue(() => runChat(req));
  }

  async function unload(): Promise<void> {
    setState({ kind: 'absent', reason: 'Add your Google Gemini API key in Settings.' });
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ensureLoaded,
    chat,
    unload,
  };
}
